import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MovingCard from './MovingCard';
import WhyDrawer from './WhyDrawer';
import AskPanel from './AskPanel';
import useObservations from './useObservations';
import ImportingNotice from './ImportingNotice';

// Home's Layer 1: "Vegetablah watches your health data and shows you what's
// moving it." The top three observations as one big card and two small ones;
// tapping any of them opens the Why drawer (every mover, the measurement
// check, your answer, Ask). The rest of the feed is one link away.
//
// This does NOT hide when there is nothing to say. The quiet state is a
// positive claim about which nothing it is, and a failed request is not
// entitled to make that claim, so it renders nothing.

const TOP = 3;

const ObservationsGlance = ({ onResolved }) => {
  const { data, loading, failed, resolve } = useObservations();
  const [whyId, setWhyId] = useState(null);
  const [askItem, setAskItem] = useState(null);

  if (loading || failed) return null;

  const items = data?.items || [];
  const top = items.slice(0, TOP);
  const [feat, ...rest] = top;
  const total = items.length;
  const importing = data?.quiet?.reason === 'importing';

  return (
    <section>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[1.75rem] font-medium leading-tight tracking-[-0.02em]">What’s moving</h1>
          <p className="mt-1 max-w-[40em] text-fg-3">
            {top.length ? `Your top ${top.length === 1 ? 'one' : top.length === 2 ? 'two' : 'three'} this week. Tap one to see more.` : 'We’ll show the biggest changes in your numbers here.'}
          </p>
        </div>
        {total > 0 && (
          <Link to="/insights" className="text-sm font-medium text-fg-2 hover:text-fg">
            See all {total} →
          </Link>
        )}
      </div>

      {top.length === 0 && importing ? (
        <ImportingNotice quiet={data.quiet} />
      ) : top.length === 0 ? (
        <div className="rounded-xl border border-line bg-raised p-5">
          <h2 className="text-lg font-medium">
            {data?.quiet?.reason === 'no_change' || data?.quiet?.reason === 'below_noise'
              ? 'Nothing unusual'
              : 'Nothing to show yet'}
          </h2>
          <p className="mt-1 text-fg-3">
            {data?.quiet?.text || "We'll say so here when something in your data moves."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3.5 lg:grid-cols-[2fr_1fr] lg:grid-rows-[auto_auto]">
          <div className="lg:row-span-2 flex">
            <MovingCard item={feat} featured onOpen={(i) => setWhyId(i.id)} />
          </div>
          {rest.map((item) => (
            <MovingCard key={item.id} item={item} onOpen={(i) => setWhyId(i.id)} />
          ))}
        </div>
      )}

      <WhyDrawer
        id={whyId}
        open={!!whyId}
        onClose={() => setWhyId(null)}
        onResolved={(id) => { resolve(id); onResolved?.(); }}
        onAsk={(d) => { setWhyId(null); setAskItem(d); }}
      />
      <AskPanel
        key={askItem?.id || 'closed'}
        open={!!askItem}
        context={askItem}
        onClose={() => setAskItem(null)}
      />
    </section>
  );
};

export default ObservationsGlance;
